import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Download, Loader2, Lock, Share2, AlertTriangle, UserCheck, UserX, FileDown, Smartphone, Monitor, Link as LinkIcon } from "lucide-react";

const Dashboard = () => {
  const { hostToken } = useParams();
  const [data, setData] = useState(null);
  const [assignments, setAssignments] = useState({}); // archetype -> friend_token
  const [locking, setLocking] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [fillOpen, setFillOpen] = useState(null); // slot number

  const refresh = () => api.get(`/host/${hostToken}`).then((r) => setData(r.data));
  useEffect(() => { refresh(); }, [hostToken]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) {
    return <div className="grain min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#8b0000]" /></div>;
  }

  const { order, group, archetypes, invite_url, kit_locked, phone_links, stage_url } = data;
  const submissions = group?.submissions || {};
  const submittedTokens = Object.keys(submissions);
  const assignedTokens = new Set(Object.values(assignments));
  const availableFriends = (t) => submittedTokens.filter((tk) => !assignedTokens.has(tk) || assignments[t] === tk);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };

  const lock = async () => {
    if (Object.keys(assignments).length !== group.size) {
      toast.error("Assign every role before locking.");
      return;
    }
    setLocking(true);
    setConflicts([]);
    try {
      const { data: res } = await api.post(`/host/${hostToken}/lock`, { assignments });
      if (res.status === "conflict") {
        setConflicts(res.conflicts);
        toast.error("Role conflict detected. Reassign and try again.");
      } else {
        toast.success("Kit locked. Check your email.");
        refresh();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lock failed");
    } finally {
      setLocking(false);
    }
  };

  const refund = async () => {
    if (!window.confirm("Refund $35 and cancel this case? Only works before locking.")) return;
    try {
      await api.post(`/host/${hostToken}/refund`);
      toast.success("Refunded.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed");
    }
  };

  const openFill = (slotNum) => setFillOpen(slotNum);

  return (
    <div className="grain min-h-screen" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">case #{order.order_id.slice(0, 8).toUpperCase()}</div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl mb-1">{order.host_name}'s dinner.</h1>
            <div className="text-[#a39797]">{order.event_date} · {order.group_size} suspects · {order.event_type}</div>
          </div>
          <div className="flex gap-2">
            <span className={`text-xs tracking-widest px-3 py-2 rounded-full border ${
              order.status === "locked" ? "border-[#d4af37] text-[#d4af37]" :
              order.status === "refunded" ? "border-[#a39797] text-[#a39797]" :
              "border-[#8b0000] text-[#8b0000]"
            }`}>
              {order.status.replace("_", " ").toUpperCase()}
            </span>
          </div>
        </div>

        {/* Invite link */}
        {!kit_locked && order.status !== "refunded" && (
          <div className="card-noir p-6 mt-8 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs tracking-widest text-[#a39797] mb-1">FRIEND INVITE LINK</div>
              <div className="font-mono text-sm text-[#f4f0ec] break-all" data-testid="invite-url">{invite_url}</div>
            </div>
            <div className="flex gap-2">
              <button className="btn-ghost text-sm" onClick={() => copy(invite_url)} data-testid="copy-invite">
                <Copy size={14} /> Copy
              </button>
              <button className="btn-primary text-sm" onClick={() => {
                if (navigator.share) navigator.share({ url: invite_url, title: "You're in the case" });
                else copy(invite_url);
              }} data-testid="share-invite"><Share2 size={14} /> Share</button>
            </div>
          </div>
        )}

        {/* Submissions */}
        <h2 className="font-display text-2xl mt-10 mb-4">Suspects ({submittedTokens.length}/{group.size})</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {group.friend_tokens.map((t, i) => {
            const sub = submissions[t];
            return (
              <div key={t} className={`card-noir p-5 ${sub ? "" : "opacity-60"}`} data-testid={`suspect-${i + 1}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-[#a39797] tracking-widest">SUSPECT #{i + 1}</div>
                    <div className="font-display text-xl mt-1">{sub?.name || "Not answered yet"}</div>
                  </div>
                  {sub ? (
                    <UserCheck size={18} className="text-[#d4af37]" />
                  ) : (
                    <UserX size={18} className="text-[#a39797]" />
                  )}
                </div>
                {sub && (
                  <div className="mt-3 space-y-1 text-sm text-[#a39797]">
                    <div><span className="text-[#8b0000]">Secret:</span> {sub.secret}</div>
                    <div><span className="text-[#8b0000]">Signature:</span> "{sub.signature}"</div>
                    <div><span className="text-[#8b0000]">Energy:</span> {sub.energy?.replace("_", " ")}</div>
                    {sub.off_limits && <div><span className="text-[#8b0000]">Off-limits:</span> {sub.off_limits}</div>}
                    {sub.filled_by_host && <div className="font-hand text-[#b30000] rotate-[-1deg] mt-1">filled by host</div>}
                  </div>
                )}
                {!sub && !kit_locked && order.status !== "refunded" && (
                  <button className="btn-ghost text-xs mt-3" onClick={() => openFill(i + 1)}
                    data-testid={`fill-suspect-${i + 1}`}>Fill in on their behalf</button>
                )}
              </div>
            );
          })}
        </div>

        {/* Role assignment */}
        {submittedTokens.length === group.size && !kit_locked && order.status !== "refunded" && (
          <>
            <h2 className="font-display text-2xl mt-10 mb-2">Assign roles</h2>
            <p className="text-[#a39797] mb-4 text-sm">Pick who plays what. We generate the plot around your choices.</p>

            {conflicts.length > 0 && (
              <div className="card-noir border-[#b30000] p-4 mb-4" data-testid="conflicts-banner">
                <div className="flex items-start gap-2 text-[#b30000]"><AlertTriangle size={18} />
                  <div>
                    <div className="font-semibold">Off-limits conflict</div>
                    {conflicts.map((c, i) => (
                      <div key={i} className="text-sm mt-1">
                        <strong>{c.friend_name}</strong> can't be <strong>{c.archetype}</strong> — {c.reason}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {archetypes.map((a) => (
                <div key={a.key} className="card-noir p-5">
                  <div className="font-display text-xl text-[#f4f0ec]">{a.title}</div>
                  <div className="text-xs text-[#a39797] mt-1 mb-3">{a.blurb.slice(0, 110)}...</div>
                  <select className="input-noir text-sm"
                    value={assignments[a.key] || ""}
                    onChange={(e) => setAssignments((prev) => ({ ...prev, [a.key]: e.target.value }))}
                    data-testid={`assign-${a.key}`}>
                    <option value="">— choose a friend —</option>
                    {availableFriends(a.key).map((t) => (
                      <option key={t} value={t}>{submissions[t].name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <button className="btn-primary" onClick={lock} disabled={locking} data-testid="lock-kit">
                {locking ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />}
                {locking ? "Locking..." : "Lock the kit"}
              </button>
              <button className="btn-ghost text-sm" onClick={refund} data-testid="refund-btn">Refund & cancel</button>
              <div className="text-xs text-[#a39797]">Full refund before you lock. No refunds after.</div>
            </div>
          </>
        )}

        {/* Downloads */}
        {kit_locked && (
          <>
            <h2 className="font-display text-2xl mt-10 mb-4">Your kit is ready.</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <a className="card-noir p-6 hover:border-[#d4af37] transition-colors block"
                href={`/kit/${hostToken}`}
                data-testid="play-on-screen">
                <Smartphone size={28} className="text-[#d4af37] mb-3" />
                <div className="font-display text-xl">Play on screen</div>
                <div className="text-sm text-[#a39797] mt-1">No printer? No problem. Pass your phone or tablet around.</div>
              </a>
              <a className="card-noir p-6 hover:border-[#d4af37] transition-colors block"
                href={`${API}/host/${hostToken}/kit/pdf`} target="_blank" rel="noopener noreferrer"
                data-testid="download-pdf">
                <FileDown size={28} className="text-[#8b0000] mb-3" />
                <div className="font-display text-xl">Printable Kit PDF</div>
                <div className="text-sm text-[#a39797] mt-1">Role cards, host script, clue sets A &amp; B, reveal. Sized for US Letter / A4.</div>
              </a>
              <a className="card-noir p-6 hover:border-[#d4af37] transition-colors block"
                href={`/kit/${hostToken}#share`}
                data-testid="download-cert">
                <Download size={28} className="text-[#d4af37] mb-3" />
                <div className="font-display text-xl">Case Closed card (9:16)</div>
                <div className="text-sm text-[#a39797] mt-1">Share to IG, TikTok, Facebook, Pinterest, Threads, X — or download for Snap.</div>
              </a>
            </div>

            {/* 4C — Event-day Host Stage */}
            {stage_url && (
              <div className="card-noir p-6 mt-6 flex items-center justify-between gap-4 flex-wrap" data-testid="stage-card">
                <div>
                  <div className="flex items-center gap-2 text-xs tracking-widest text-[#d4af37] mb-1"><Monitor size={14} /> EVENT-DAY STAGE</div>
                  <div className="font-display text-xl">Big-screen host view</div>
                  <div className="text-sm text-[#a39797] mt-1">Timer, reveal controls, live scoreboard — cast this to the room.</div>
                </div>
                <a href={`/stage/${hostToken}`} className="btn-primary" target="_blank" rel="noopener noreferrer" data-testid="open-stage">
                  <Monitor size={16} /> Open stage
                </a>
              </div>
            )}

            {/* 3A — Private phone links */}
            {phone_links && phone_links.length > 0 && (
              <div className="mt-10" data-testid="phone-links-section">
                <h2 className="font-display text-2xl mb-2 flex items-center gap-2">
                  <Smartphone size={22} className="text-[#d4af37]" /> Private phone links
                </h2>
                <p className="text-sm text-[#a39797] mb-4">
                  Text each guest their own link. Only they see their role card.
                  Clues &amp; reveals push live from your Stage view.
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  {phone_links.map((p) => (
                    <div key={p.player_token} className="card-noir p-4" data-testid={`phone-link-${p.player_token}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-display text-lg truncate">{p.player_name}</div>
                          <div className="text-xs text-[#a39797]">{p.archetype}</div>
                          <div className="font-mono text-xs text-[#f4f0ec] break-all mt-2">{p.play_url}</div>
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button className="btn-ghost text-xs" onClick={() => copy(p.play_url)}
                            data-testid={`copy-player-${p.player_token}`}>
                            <Copy size={12} /> Copy
                          </button>
                          <a className="btn-ghost text-xs"
                            href={`sms:?&body=${encodeURIComponent(`your private link for tonight's case: ${p.play_url}`)}`}
                            data-testid={`sms-player-${p.player_token}`}>
                            <LinkIcon size={12} /> SMS
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {fillOpen && (
          <FillOnBehalf hostToken={hostToken} slotNum={fillOpen} onClose={() => { setFillOpen(null); refresh(); }} />
        )}
      </div>
    </div>
  );
};

const FillOnBehalf = ({ hostToken, slotNum, onClose }) => {
  const [form, setForm] = useState({ name: "", secret: "", signature: "", energy: "charming_liar", off_limits: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/host/${hostToken}/fill-friend`, { friend_slot: slotNum, ...form });
      toast.success("Saved.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-noir p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="fill-modal">
        <div className="font-display text-2xl mb-4">Fill in Suspect #{slotNum}</div>
        <form onSubmit={submit} className="space-y-3">
          <input className="input-noir" placeholder="Friend's name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="fill-name" />
          <textarea className="input-noir min-h-[70px]" placeholder="Harmless secret" value={form.secret}
            onChange={(e) => setForm({ ...form, secret: e.target.value })} required data-testid="fill-secret" />
          <textarea className="input-noir min-h-[70px]" placeholder="Signature line" value={form.signature}
            onChange={(e) => setForm({ ...form, signature: e.target.value })} required data-testid="fill-signature" />
          <select className="input-noir" value={form.energy}
            onChange={(e) => setForm({ ...form, energy: e.target.value })} data-testid="fill-energy">
            <option value="charming_liar">Charming liar</option>
            <option value="loyal_sidekick">Loyal sidekick</option>
            <option value="chaos_agent">Chaos agent</option>
            <option value="mysterious_stranger">Mysterious stranger</option>
          </select>
          <input className="input-noir" placeholder="Off-limits (optional)" value={form.off_limits}
            onChange={(e) => setForm({ ...form, off_limits: e.target.value })} data-testid="fill-off-limits" />
          <input type="email" className="input-noir" placeholder="Their email (optional — auto-sends role on lock)" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="fill-email" />
          <input type="tel" className="input-noir" placeholder="Their phone +E.164 (optional — SMS the role too)" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="fill-phone" />
          <div className="flex gap-2 pt-2">
            <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary text-sm" disabled={loading} data-testid="fill-submit">
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
          <div className="font-hand text-[#b30000] rotate-[-1deg] text-sm pt-1">this will be marked "filled by host"</div>
        </form>
      </div>
    </div>
  );
};

export default Dashboard;
